import React, { useState, useRef, useEffect } from 'react';
import toast from 'react-hot-toast';
import { ownerPortal } from '../services/api';
import { useOwnerAuth } from '../context/OwnerAuthContext';

const SUGGESTED = [
  'What are the garbage and recycling pickup days?',
  'What are the quiet hours in the building?',
  'Can I make renovations to my unit?',
  'What are the pet rules in this building?',
  'Can I rent out my unit on Airbnb?',
  'When is the next strata meeting?',
  'What does my monthly strata fee cover?',
  'How do I request a parking stall?',
];

// Tiny markdown renderer: **bold**, *italic*, bullet lines (- or •)
function RenderMD({ text }) {
  if (!text) return null;
  return (
    <div className="space-y-1">
      {text.split('\n').map((line, i) => {
        const isBullet = /^[\-•]\s+/.test(line.trim());
        const parts = line.replace(/^[\-•]\s+/, '').split(/(\*\*[^*]+\*\*)/g);
        const content = parts.map((p, j) =>
          p.startsWith('**') && p.endsWith('**')
            ? <strong key={j}>{p.slice(2, -2)}</strong>
            : p
        );
        if (isBullet) {
          return (
            <div key={i} className="flex gap-1.5">
              <span className="mt-0.5 shrink-0 text-current opacity-60">•</span>
              <span>{content}</span>
            </div>
          );
        }
        return line.trim() ? <p key={i}>{content}</p> : <div key={i} className="h-1" />;
      })}
    </div>
  );
}

function TypingDots() {
  return (
    <div className="flex gap-3 items-end mb-5">
      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0 text-base">🤖</div>
      <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
        <div className="flex gap-1 items-center h-4">
          {[0, 150, 300].map(d => (
            <span key={d} className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
              style={{ animationDelay: `${d}ms` }} />
          ))}
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ msg, onEscalate, onFollowUp }) {
  const isUser = msg.role === 'user';

  return (
    <div className={`flex gap-3 items-end mb-5 ${isUser ? 'flex-row-reverse' : ''}`}>
      {!isUser && (
        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0 text-base select-none">
          🤖
        </div>
      )}

      <div className={`flex flex-col gap-2 max-w-[82%] ${isUser ? 'items-end' : 'items-start'}`}>
        {/* Bubble */}
        <div className={`rounded-2xl px-4 py-3 text-sm shadow-sm ${
          isUser
            ? 'bg-blue-600 text-white rounded-br-sm'
            : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm'
        }`}>
          {isUser
            ? <p>{msg.content}</p>
            : <RenderMD text={msg.content} />}
        </div>

        {/* Escalation card */}
        {!isUser && msg.escalation?.should_escalate && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 text-sm w-full">
            <p className="font-semibold text-orange-800 mb-1 flex items-center gap-1.5">
              <span>🔔</span> This may need your property manager
            </p>
            {msg.escalation.escalation_reason && (
              <p className="text-orange-700 text-xs mb-3 leading-relaxed">
                {msg.escalation.escalation_reason}
              </p>
            )}
            {msg.escalated ? (
              <p className="text-green-700 text-xs font-semibold flex items-center gap-1">
                <span>✅</span> Manager has been notified!
              </p>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => onEscalate(msg)}
                  className="px-3 py-1.5 bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white text-xs font-medium rounded-lg transition-colors"
                >
                  Yes, notify manager
                </button>
                <button
                  onClick={() => onEscalate(msg, true)}
                  className="px-3 py-1.5 bg-white border border-orange-200 hover:bg-orange-50 text-orange-700 text-xs font-medium rounded-lg transition-colors"
                >
                  No thanks
                </button>
              </div>
            )}
          </div>
        )}

        {/* Follow-up suggestions */}
        {!isUser && msg.follow_ups?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 max-w-full">
            {msg.follow_ups.map((q, i) => (
              <button
                key={i}
                onClick={() => onFollowUp(q)}
                className="text-xs px-3 py-1.5 bg-blue-50 hover:bg-blue-100 active:bg-blue-200 text-blue-700 rounded-full border border-blue-100 transition-colors"
              >
                {q}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function OwnerChat() {
  const { owner, unit } = useOwnerAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to latest message
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const sendMessage = async (text) => {
    const question = (text || input).trim();
    if (!question || loading) return;

    setInput('');
    const userMsg = { role: 'user', content: question, id: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    // Build history for context (exclude escalation metadata)
    const history = messages.map(m => ({ role: m.role, content: m.content }));

    try {
      const res = await ownerPortal.chat({ question, history });
      const aiMsg = {
        role: 'assistant',
        content: res.data.answer,
        escalation: {
          should_escalate: res.data.should_escalate,
          escalation_reason: res.data.escalation_reason,
        },
        follow_ups: res.data.suggested_follow_ups || [],
        escalated: false,
        id: Date.now() + 1,
      };
      setMessages(prev => [...prev, aiMsg]);
    } catch (err) {
      toast.error('Sorry, I couldn\'t get a response. Please try again.');
      // Remove the user message on failure
      setMessages(prev => prev.filter(m => m.id !== userMsg.id));
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleEscalate = async (targetMsg, dismiss = false) => {
    if (dismiss) {
      setMessages(prev => prev.map(m =>
        m.id === targetMsg.id ? { ...m, escalated: true } : m
      ));
      return;
    }
    try {
      // Create a maintenance request to flag the conversation to the manager
      await ownerPortal.submitMaintenance({
        title: '🔔 Owner requested manager contact via AI Assistant',
        description:
          `Owner question: "${targetMsg.content || 'See chat context'}"\n\n` +
          `AI reason for escalation: ${targetMsg.escalation?.escalation_reason || 'Complex query requiring manager review.'}\n\n` +
          `Please follow up with the owner.`,
        category: 'other',
        priority: 'medium',
      });
      setMessages(prev => prev.map(m =>
        m.id === targetMsg.id ? { ...m, escalated: true } : m
      ));
      toast.success('Manager notified — they\'ll follow up with you shortly');
    } catch {
      toast.error('Could not notify manager. Please contact them directly.');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const isEmpty = messages.length === 0;

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] md:h-[calc(100vh-4rem)]">
      {/* Header */}
      <div className="mb-4 shrink-0">
        <h1 className="text-2xl font-bold text-gray-900">AI Assistant</h1>
        <p className="text-gray-500 text-sm mt-1">
          Ask anything about your building, bylaws, fees, or maintenance
        </p>
      </div>

      {/* Chat area */}
      <div className="flex-1 bg-white rounded-2xl border border-gray-200 overflow-hidden flex flex-col shadow-sm">

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-5 md:px-6">

          {/* Welcome / empty state */}
          {isEmpty && (
            <div className="flex flex-col items-center text-center py-6 mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center text-3xl mb-4 shadow-inner">
                🤖
              </div>
              <h2 className="text-lg font-semibold text-gray-900">
                Hi {owner?.first_name}! I'm your strata assistant.
              </h2>
              <p className="text-gray-500 text-sm mt-1 max-w-sm">
                I can answer questions about{' '}
                {unit ? `Unit ${unit.unit_number} and ` : ''}your building's
                bylaws, rules, fees, and maintenance. Ask me anything!
              </p>
            </div>
          )}

          {/* Suggested questions (only when empty) */}
          {isEmpty && (
            <div className="mb-6">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3 text-center">
                Common questions
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                {SUGGESTED.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(q)}
                    disabled={loading}
                    className="text-sm px-4 py-2 bg-gray-50 hover:bg-blue-50 active:bg-blue-100 border border-gray-200 hover:border-blue-200 text-gray-700 hover:text-blue-700 rounded-full transition-colors disabled:opacity-50"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Message list */}
          {messages.map((msg) => (
            <MessageBubble
              key={msg.id}
              msg={msg}
              onEscalate={handleEscalate}
              onFollowUp={(q) => sendMessage(q)}
            />
          ))}

          {/* Typing indicator */}
          {loading && <TypingDots />}

          <div ref={bottomRef} />
        </div>

        {/* Input bar */}
        <div className="border-t border-gray-100 px-4 py-3 bg-gray-50/60 shrink-0">
          <div className="flex gap-2 items-end">
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
              rows={1}
              placeholder="Ask about bylaws, rules, fees, maintenance…"
              className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 resize-none bg-white disabled:opacity-50 transition-all"
              style={{ minHeight: 44, maxHeight: 120 }}
              onInput={e => {
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
              }}
            />
            <button
              onClick={() => sendMessage()}
              disabled={loading || !input.trim()}
              className="w-11 h-11 rounded-xl bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
            >
              {loading ? (
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              )}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-1.5 text-center">
            Press Enter to send · Shift+Enter for new line
          </p>
        </div>
      </div>
    </div>
  );
}
